import { clamp } from "../../core/math";
import { scheduleAttack, scheduleRelease, smoothSet, type Adsr } from "../envelope";
import { HARD_MAX_VOICES, type Instrument } from "./types";

export interface MultiSampleParams {
  volume: number;
  attackSec: number;
  releaseSec: number;
}

export const DEFAULT_MULTISAMPLE_PARAMS: MultiSampleParams = { volume: 0.8, attackSec: 0.002, releaseSec: 0.35 };

/** One recorded note: plays for MIDI notes lo..hi, pitched relative to `root`. */
export interface SampleZone {
  root: number;
  lo: number;
  hi: number;
  buffer: AudioBuffer;
  /** Seconds of leading silence to skip (MP3 encoder delay, pre-roll). */
  offsetSec: number;
}

export interface SampleSet {
  zones: SampleZone[];
  /** Whole-instrument tuning correction in cents. */
  tuneCents?: number;
  /** Drums: one-shots (note-off ignored), no pitch bend. */
  oneShot?: boolean;
}

interface MVoice { note: number; root: number; src: AudioBufferSourceNode; amp: GainNode; startSec: number; released: boolean; }

const MAX_BEND = 24;

/**
 * Multi-sampled instrument (real recordings): picks the zone covering the
 * note and repitches it by playbackRate = 2^(Δ/12). Zones are at most a few
 * semitones apart, so the duration change is small (AGENT.md §12).
 * Silent until `setSamples` is called (samples load asynchronously).
 */
export class MultiSampleInstrument implements Instrument<MultiSampleParams> {
  readonly kind = "multisample";
  readonly output: GainNode;
  private _params: MultiSampleParams;
  private set: SampleSet | null = null;
  private voices: MVoice[] = [];
  private bendSemitones = 0;

  constructor(private ctx: BaseAudioContext, params: Partial<MultiSampleParams> = {}) {
    this._params = { ...DEFAULT_MULTISAMPLE_PARAMS, ...params };
    this.output = ctx.createGain();
    this.output.gain.value = this._params.volume;
  }

  get params(): MultiSampleParams { return this._params; }
  get activeVoices(): number { return this.voices.length; }
  get loaded(): boolean { return this.set !== null; }

  setSamples(set: SampleSet | null): void {
    this.hardStop();
    this.set = set;
  }

  /** Zone for a note: the one whose range contains it, else the nearest root. */
  static zoneFor(zones: readonly SampleZone[], note: number): SampleZone | undefined {
    let best: SampleZone | undefined;
    for (const z of zones) {
      if (note >= z.lo && note <= z.hi) return z;
      if (!best || Math.abs(z.root - note) < Math.abs(best.root - note)) best = z;
    }
    return best;
  }

  private rate(note: number, root: number): number {
    const cents = (note + (this.set?.oneShot ? 0 : this.bendSemitones) - root) * 100 + (this.set?.tuneCents ?? 0);
    return Math.pow(2, cents / 1200);
  }

  noteOn(note: number, velocity: number, timeSec = this.ctx.currentTime): void {
    if (!this.set) return;
    const zone = MultiSampleInstrument.zoneFor(this.set.zones, note);
    if (!zone) return;
    if (this.set.oneShot && (note < zone.lo || note > zone.hi)) return; // unmapped drum note
    while (this.voices.length >= HARD_MAX_VOICES) this.stopVoice(this.voices[0]!, timeSec);
    const src = this.ctx.createBufferSource();
    src.buffer = zone.buffer;
    src.playbackRate.value = this.rate(note, zone.root);
    const amp = this.ctx.createGain();
    amp.gain.value = 0;
    src.connect(amp).connect(this.output);
    // Velocity → level on a gentle curve (one recorded dynamic layer).
    scheduleAttack(amp.gain, this.env(), Math.pow(clamp(velocity, 0, 1), 1.5), timeSec);
    src.start(timeSec, zone.offsetSec);
    const v: MVoice = { note, root: zone.root, src, amp, startSec: timeSec, released: false };
    src.onended = () => this.cleanup(v);
    this.voices.push(v);
  }

  noteOff(note: number, timeSec = this.ctx.currentTime): void {
    if (this.set?.oneShot) return;
    for (const v of this.voices) if (v.note === note && !v.released) this.release(v, timeSec);
  }

  releaseAll(timeSec = this.ctx.currentTime): void {
    if (this.set?.oneShot) return;
    for (const v of this.voices) if (!v.released) this.release(v, timeSec);
  }

  hardStop(): void {
    const now = this.ctx.currentTime;
    for (const v of [...this.voices]) this.stopVoice(v, now);
  }

  pitchBend(semitones: number): void {
    this.bendSemitones = clamp(semitones, -MAX_BEND, MAX_BEND);
    if (this.set?.oneShot) return;
    for (const v of this.voices) smoothSet(v.src.playbackRate, this.rate(v.note, v.root), this.ctx, 0.005);
  }

  setParams(params: Partial<MultiSampleParams>): void {
    this._params = { ...this._params, ...params };
    if (params.volume !== undefined) smoothSet(this.output.gain, this._params.volume, this.ctx);
  }

  dispose(): void {
    this.hardStop();
    this.output.disconnect();
    this.set = null;
  }

  private env(): Adsr {
    return { attackSec: this._params.attackSec, decaySec: 0.001, sustain: 1, releaseSec: this._params.releaseSec };
  }

  private release(v: MVoice, timeSec: number): void {
    v.released = true;
    const end = scheduleRelease(v.amp.gain, this.env(), 1, v.startSec, Math.max(timeSec, v.startSec));
    try { v.src.stop(end); } catch { /* already stopped */ }
  }

  private stopVoice(v: MVoice, timeSec: number): void {
    v.amp.gain.cancelScheduledValues(timeSec);
    v.amp.gain.setTargetAtTime(0, timeSec, 0.004);
    try { v.src.stop(timeSec + 0.02); } catch { /* already stopped */ }
    this.voices = this.voices.filter((x) => x !== v);
  }

  private cleanup(v: MVoice): void {
    this.voices = this.voices.filter((x) => x !== v);
    v.src.disconnect();
    v.amp.disconnect();
  }
}

/**
 * Seconds of leading near-silence in a decoded sample (MP3 encoder delay
 * differs between browsers' decoders), backed off slightly to keep the attack.
 */
export function leadingSilenceSec(buffer: AudioBuffer, threshold = 0.003, backoffSec = 0.002): number {
  const n = buffer.length;
  let first = n;
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const d = buffer.getChannelData(c);
    for (let i = 0; i < Math.min(first, n); i++) if (Math.abs(d[i]!) > threshold) { first = i; break; }
  }
  if (first >= n) return 0;
  return Math.max(0, first / buffer.sampleRate - backoffSec);
}
